/**MTNPddReadiness.js — PDD & Verification Readiness — 11-stage pipeline */
import React, { useState, useEffect } from "react";
import { useMTN } from "@/context/MTNContext";
import client from "@/api/client";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, DollarSign, Clock } from "lucide-react";
import ExecutiveActionPanel from "@/components/ExecutiveActionPanel";

const fmtN=(v,u="")=>{if(!v&&v!==0)return"—";const n=Number(v);if(n>=1e3)return`${(n/1e3).toFixed(0)}K ${u}`.trim();return`${n.toLocaleString()} ${u}`.trim();};
const fmtU=v=>{if(!v&&v!==0)return"—";const n=Number(v);if(n>=1e6)return`$${(n/1e6).toFixed(1)}M`;if(n>=1e3)return`$${(n/1e3).toFixed(0)}K`;return`$${n}`;};
const STAGES=["Opportunity ID","Eligibility Screening","Baseline Assessment","Additionality Analysis","Monitoring Plan","Leakage Assessment","Stakeholder Consult","Financial Structuring","PDD Drafting","Validation","Registry Submission","Issuance"];

export default function MTNPddReadiness() {
  const { selectedPeriod, loading } = useMTN();
  const [ci, setCi] = useState(null);
  const [loadingC, setLoadingC] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(()=>{
    if(!selectedPeriod)return;
    setLoadingC(true);
    client.get(`/mtn/carbon-intelligence/${selectedPeriod}`)
      .then(r=>setCi(r.data)).catch(()=>{}).finally(()=>setLoadingC(false));
  },[selectedPeriod]);

  if(loading||loadingC)return<div style={{padding:40,color:"#64748b",fontSize:12}}>Loading PDD intelligence…</div>;
  if(!ci)return<div style={{padding:40,color:"#64748b",fontSize:12}}>No data.</div>;

  const sig=ci.ssot_signals||{};

  return(
    <div style={{padding:"24px 28px"}}>
      <div style={{marginBottom:20}}>
        <span style={{fontSize:9,fontWeight:800,color:"#FFCB00",letterSpacing:".08em",textTransform:"uppercase",background:"#FFCB0015",border:"1px solid #FFCB0030",padding:"2px 8px",borderRadius:4}}>MTN Nigeria</span>
        <h1 style={{fontSize:20,fontWeight:900,color:"#f1f5f9",marginTop:6}}>PDD & Verification Readiness</h1>
        <p style={{fontSize:11,color:"#475569"}}>11-stage pipeline tracker · NCCC readiness · DOE/VVB readiness · Evidence blockers</p>
      </div>

      {/* PDD readiness summary */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
        {[["PDD Overall Score",`${ci.pdd_overall_score}/100`,ci.pdd_overall_score>=65?"#22c55e":ci.pdd_overall_score>=50?"#f59e0b":"#ef4444"],
          ["VVB Pre-qual",ci.pdd_validation_ready?"Ready":"Not Ready",ci.pdd_validation_ready?"#22c55e":"#f59e0b"],
          ["Issuance Probability",ci.issuance_probability,"#60a5fa"],
          ["Art.6 Overall",`${ci.article6_overall}/100`,"#a78bfa"],
        ].map(([l,v,c])=>(
          <div key={l} style={{background:"#0d1423",border:`1px solid ${c}25`,borderRadius:8,padding:"12px 14px"}} data-testid={`pdd-metric-${l.toLowerCase().replace(/\s+/g,"-")}`}>
            <p style={{fontSize:8,color:"#475569",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{l}</p>
            <p style={{fontSize:18,fontWeight:900,color:c}}>{v}</p>
          </div>
        ))}
      </div>

      {/* NCCC & LoA readiness */}
      <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16,marginBottom:14}} data-testid="nccc-readiness">
        <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>NCCC / LoA / DNA Readiness Status</p>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
          {[["NCCC Engagement","Not Started",8,"#ef4444","Immediate: Initiate formal NCCC contact"],
            ["Letter of Authorisation (LoA)","Not Initiated",5,"#ef4444","Requires NCCC engagement first"],
            ["DNA Contact","Not Established",10,"#ef4444","Critical first step for Art.6"],
            ["Corresponding Adjustment","Under Review",22,"#f59e0b","CA requires host country consent"],
            ["DOE/VVB Engagement","Pre-screening Stage",35,"#f59e0b","Commission VVB pre-screening once baseline complete"],
            ["NDC Alignment Mapping","Partial",52,"#f59e0b","Formal mapping to Nigeria NDC required"],
          ].map(([l,s,sc,c,act])=>(
            <div key={l} style={{background:"#060c1a",border:`1px solid ${c}20`,borderRadius:6,padding:12}}>
              <p style={{fontSize:9,fontWeight:700,color:"#f1f5f9",marginBottom:2}}>{l}</p>
              <p style={{fontSize:8,color:c,fontWeight:600,marginBottom:4}}>{s}</p>
              <div style={{height:4,background:"#1e293b",borderRadius:2,overflow:"hidden",marginBottom:4}}><div style={{height:"100%",width:`${sc}%`,background:c,borderRadius:2}}/></div>
              <p style={{fontSize:8,color:"#60a5fa"}}>{act}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cost-to-issuance estimates */}
      <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16,marginBottom:14}} data-testid="cost-to-issuance">
        <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Cost-to-Issuance Estimates — Institutional PoA Structure</p>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
          {[["Baseline Study","$80-150K","Independent consultant. Tower fuel metering + grid baseline.","#60a5fa"],
            ["PDD Preparation","$150-250K","Accredited DOE. PoA structure for 18,500 sites.","#FFCB00"],
            ["VVB Validation","$80-120K","Accredited VVB. Estimated 4-6 months.","#f59e0b"],
            ["Verification (Year 1)","$60-90K/yr","Annual monitoring report + VVB verification.","#22c55e"],
          ].map(([l,v,n,c])=>(
            <div key={l} style={{background:"#060c1a",border:`1px solid ${c}25`,borderRadius:6,padding:12}}>
              <p style={{fontSize:9,fontWeight:700,color:"#f1f5f9",marginBottom:2}}>{l}</p>
              <p style={{fontSize:13,fontWeight:900,color:c,marginBottom:3}}>{v}</p>
              <p style={{fontSize:8,color:"#475569"}}>{n}</p>
            </div>
          ))}
        </div>
        <div style={{background:"#1e3a5f30",border:"1px solid #60a5fa30",borderRadius:6,padding:10,marginTop:10}}>
          <p style={{fontSize:9,color:"#60a5fa"}}><span style={{fontWeight:700}}>Total preparation cost (indicative):</span> $370-610K | Break-even at ~25,000 tCO₂e at $18/t = $450K/yr | PoA registration fee: $15-30K at Verra VCS</p>
        </div>
      </div>

      {/* Dependency chain */}
      <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16,marginBottom:14}} data-testid="dependency-chain">
        <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Dependency Chain — Critical Path to Issuance</p>
        <div style={{display:"flex",alignItems:"center",gap:0,overflowX:"auto"}}>
          {[
            {step:"1. Smart Metering",time:"6mo",color:"#ef4444",status:"Not Started"},
            {step:"2. T1 Evidence",time:"3mo",color:"#f59e0b",status:"In Progress"},
            {step:"3. Baseline Study",time:"4mo",color:"#f59e0b",status:"Not Started"},
            {step:"4. PoA Structure",time:"3mo",color:"#60a5fa",status:"Pipeline"},
            {step:"5. PDD Draft",time:"4mo",color:"#60a5fa",status:"Pipeline"},
            {step:"6. VVB Validation",time:"5mo",color:"#a78bfa",status:"Pipeline"},
            {step:"7. Registry",time:"2mo",color:"#22c55e",status:"Pipeline"},
            {step:"8. Issuance",time:"1mo",color:"#22c55e",status:"Target"},
          ].map((s,i)=>(
            <React.Fragment key={s.step}>
              <div style={{flexShrink:0,textAlign:"center",minWidth:80}}>
                <div style={{height:28,width:28,borderRadius:"50%",background:s.color+"20",border:`2px solid ${s.color}`,
                  display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 4px",
                  fontSize:10,fontWeight:800,color:s.color}}>{i+1}</div>
                <p style={{fontSize:7,color:"#94a3b8",marginBottom:1}}>{s.step.split(".")[1].trim()}</p>
                <p style={{fontSize:7,color:s.color,fontWeight:600}}>{s.time}</p>
                <p style={{fontSize:6,color:s.status==="Not Started"?"#ef4444":s.status==="In Progress"?"#FFCB00":"#475569"}}>{s.status}</p>
              </div>
              {i<7 && <div style={{flex:1,height:2,background:"#1e293b",minWidth:12}}/>}
            </React.Fragment>
          ))}
        </div>
        <p style={{fontSize:8,color:"#475569",marginTop:8}}>Total critical path: ~28 months from smart metering deployment to first credit issuance. Art.6 pathway adds 12-18 months.</p>
      </div>

      <ExecutiveActionPanel compact={true} title="PDD Origination Actions" actions={[
        {severity:"Critical",category:"PDD",title:"Smart metering deployment is the critical path blocker",description:"T1 evidence upgrade from smart metering is prerequisite for all carbon market pathways.",actionRequired:"Deploy smart meters at top 500 diesel-heavy towers immediately",financialExposure:"$450K/yr carbon revenue blocked",timeline:"Q2 2026",owner:"CTO",blocker:"CAPEX allocation pending board approval"},
        {severity:"High",category:"PDD",title:"Baseline study not yet commissioned",description:"Independent baseline assessment required before PDD development can begin.",actionRequired:"Procure baseline study consultant — issue RFP now",timeline:"Q3 2026",owner:"Sustainability + CFO",valueUpside:"Unlocks PoA registration timeline"},
      ]} />

      {/* PDD Pipeline */}
      <div style={{display:"flex",flexDirection:"column",gap:10}} data-testid="pdd-pipeline-full">
        <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em"}}>11-Stage PDD Pipeline — Carbon-Eligible Projects</p>
        {(ci.pdd_pipeline||[]).length===0 && (
          <div style={{background:"#0d1423",borderRadius:8,padding:24,textAlign:"center"}}>
            <p style={{color:"#475569",fontSize:12}}>No carbon-pathway projects. Assign carbon_pathway in Data Workspace.</p>
          </div>
        )}
        {(ci.pdd_pipeline||[]).map(p=>(
          <div key={p.project_id} style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}} data-testid={`pdd-full-${p.project_id}`}>
            <div style={{padding:14}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                    <span style={{fontSize:11,fontWeight:800,color:"#f1f5f9"}}>{p.name}</span>
                    <span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:3,border:`1px solid ${p.pathway==="art6"?"#a78bfa40":"#22c55e40"}`,background:p.pathway==="art6"?"#4c1d9520":"#14532d20",color:p.pathway==="art6"?"#a78bfa":"#22c55e"}}>{p.pathway==="art6"?"Art.6 — Sovereign Auth.":"VCM"}</span>
                  </div>
                  <p style={{fontSize:9,color:"#64748b"}}>Current: Stage {p.current_stage}/11 — {p.stage_label} · Readiness: {p.readiness_pct}%</p>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <p style={{fontSize:8,color:"#475569"}}>Issuance Potential</p>
                  <p style={{fontSize:16,fontWeight:900,color:"#60a5fa"}}>{fmtN(p.issue_potential_tco2e,"tCO₂e")}</p>
                  <p style={{fontSize:8,color:"#64748b"}}>{p.timeline_yr}yr to issuance</p>
                </div>
              </div>

              {/* 12-stage bar */}
              <div style={{marginBottom:8}}>
                <div style={{display:"flex",gap:1,marginBottom:3}}>
                  {STAGES.map((st,si)=>(
                    <div key={si} title={`${si+1}. ${st}`}
                      style={{flex:1,height:8,borderRadius:1,background:si<p.current_stage?"#22c55e":si===p.current_stage?"#FFCB00":"#1e293b",cursor:"default"}}/>
                  ))}
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{fontSize:7,color:"#475569"}}>Opportunity ID</span>
                  <span style={{fontSize:7,color:"#FFCB00",fontWeight:600}}>{p.stage_label}</span>
                  <span style={{fontSize:7,color:"#475569"}}>Issuance</span>
                </div>
              </div>

              <div style={{background:"#1e3a5f30",border:"1px solid #60a5fa30",borderRadius:5,padding:8,marginBottom:8}}>
                <p style={{fontSize:8,fontWeight:700,color:"#60a5fa",marginBottom:2}}>Next Required Action</p>
                <p style={{fontSize:9,color:"#f1f5f9"}}>{p.next_action}</p>
              </div>

              <button onClick={()=>setExpanded(prev=>({...prev,[p.project_id]:!prev[p.project_id]}))}
                style={{display:"flex",alignItems:"center",gap:5,fontSize:9,color:"#64748b",background:"none",border:"none",cursor:"pointer"}}>
                {expanded[p.project_id]?<ChevronUp size={10}/>:<ChevronDown size={10}/>}
                Evidence & Process Blockers ({p.blockers?.length||0})
              </button>
            </div>
            {expanded[p.project_id] && (
              <div style={{background:"#060c1a",borderTop:"1px solid #1e293b",padding:12}}>
                <p style={{fontSize:8,fontWeight:700,color:"#475569",textTransform:"uppercase",marginBottom:6}}>Evidence & Process Blockers</p>
                {(p.blockers||[]).map((b,i)=>(<div key={i} style={{display:"flex",gap:5,marginBottom:4}}><AlertTriangle size={9} color="#f59e0b" style={{flexShrink:0}}/><p style={{fontSize:9,color:"#94a3b8"}}>{b}</p></div>))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
